<section class="page-head">
  <div>
    <h1>{{ 'admin.docs_title' | translate }}</h1>
    <p>{{ 'admin.docs_subtitle' | translate }}</p>
  </div>
  <div class="head-actions">
    <button type="button" class="dm-a-btn ghost" (click)="newFolder()">
      <i-tabler name="folder-plus" class="icon-18"></i-tabler>{{ 'admin.docs_new_folder' | translate }}
    </button>
    <input #fileInput type="file" accept="application/pdf,.pdf" multiple hidden (change)="onFileSelected($event)"/>
    <button type="button" class="dm-a-btn primary" (click)="fileInput.click()" [disabled]="uploading > 0">
      @if (uploading > 0) {
        <mat-spinner diameter="16"></mat-spinner>{{ 'documents.uploading' | translate }} ({{ uploading }})
      } @else {
        <i-tabler name="upload" class="icon-18"></i-tabler>{{ 'admin.docs_upload' | translate }}
      }
    </button>
  </div>
</section>

<nav class="crumbs" [attr.aria-label]="'admin.docs_path' | translate">
  <button type="button" (click)="goTo(0)" [class.current]="path.length === 0">
    <i-tabler name="home" class="icon-16"></i-tabler>{{ 'admin.docs_root' | translate }}
  </button>
  @for (folder of path; track folder.id; let i = $index) {
    <i-tabler name="chevron-right" class="icon-16 sep"></i-tabler>
    <button type="button" (click)="goTo(i + 1)" [class.current]="i === path.length - 1">{{ folder.name }}</button>
  }
</nav>

@if (error) {
  <p class="dm-a-error" role="alert">{{ error }}</p>
}

<div class="card drop-card" [class.drag]="dragOver" (dragover)="onDragOver($event)" (dragleave)="dragOver = false" (drop)="onDrop($event)">
  @if (loading) {
    <div class="state"><mat-spinner diameter="32"></mat-spinner></div>
  } @else if (items.length === 0) {
    <div class="state">
      <i-tabler name="cloud-upload" class="icon-32"></i-tabler>
      <p>{{ 'admin.docs_empty' | translate }}</p>
      <button type="button" class="dm-a-btn primary" (click)="fileInput.click()">{{ 'admin.docs_upload' | translate }}</button>
    </div>
  } @else {
    <table class="data-table">
      <thead>
        <tr>
          <th>{{ 'admin.docs_name' | translate }}</th>
          <th class="hide-md">{{ 'admin.docs_added' | translate }}</th>
          <th>{{ 'admin.status' | translate }}</th>
          <th class="ai-col">{{ 'admin.docs_ai' | translate }}</th>
          <th class="actions-col"><span class="sr-only">{{ 'admin.actions' | translate }}</span></th>
        </tr>
      </thead>
      <tbody>
        @for (folder of folders; track folder.id) {
          <tr class="folder-row">
            <td>
              <button type="button" class="item-link" (click)="openFolder(folder)">
                <span class="item-icon folder"><i-tabler name="folder" class="icon-20"></i-tabler></span>
                <strong>{{ folder.name }}</strong>
              </button>
            </td>
            <td class="hide-md">{{ folder.created_at | date: 'dd/MM/yyyy' }}</td>
            <td></td>
            <td class="ai-col"></td>
            <td class="actions-col">
              <button type="button" class="icon-btn" [matMenuTriggerFor]="folderMenu" [attr.aria-label]="'admin.actions' | translate">
                <i-tabler name="dots-vertical" class="icon-18"></i-tabler>
              </button>
              <mat-menu #folderMenu="matMenu" xPosition="before">
                <button mat-menu-item (click)="openFolder(folder)"><i-tabler name="folder-open" class="icon-18 m-r-8"></i-tabler>{{ 'admin.docs_open' | translate }}</button>
                <button mat-menu-item (click)="rename(folder)"><i-tabler name="pencil" class="icon-18 m-r-8"></i-tabler>{{ 'admin.docs_rename' | translate }}</button>
                <button mat-menu-item (click)="move(folder)"><i-tabler name="folder-symlink" class="icon-18 m-r-8"></i-tabler>{{ 'admin.docs_move' | translate }}</button>
                <button mat-menu-item (click)="remove(folder)"><i-tabler name="trash" class="icon-18 m-r-8"></i-tabler>{{ 'admin.docs_delete' | translate }}</button>
              </mat-menu>
            </td>
          </tr>
        }
        @for (doc of files; track doc.id) {
          <tr>
            <td>
              <button type="button" class="item-link" (click)="openPdf(doc)" [matTooltip]="'documents.open_pdf' | translate">
                <span class="item-icon pdf"><i-tabler name="file-type-pdf" class="icon-20"></i-tabler></span>
                <span class="item-text">
                  <strong>{{ doc.name }}</strong>
                  <span>{{ formatSize(doc.dimension_file) }}</span>
                </span>
              </button>
            </td>
            <td class="hide-md">{{ doc.created_at | date: 'dd/MM/yyyy' }}</td>
            <td>
              <span class="dm-a-badge rag-{{ doc.rag_status }}" [matTooltip]="doc.rag_status === 'error' ? (doc.rag_error || '') : ''">
                @if (isBusy(doc)) {
                  <mat-spinner diameter="10" class="badge-spinner"></mat-spinner>
                }
                {{ 'admin.docs_status_' + doc.rag_status | translate }}
              </span>
            </td>
            <td class="ai-col">
              <mat-slide-toggle [checked]="isAiOn(doc)" [disabled]="isBusy(doc)" (change)="toggleAi(doc, $event.checked)"
                                [attr.aria-label]="'admin.docs_ai' | translate"></mat-slide-toggle>
            </td>
            <td class="actions-col">
              <button type="button" class="icon-btn" [matMenuTriggerFor]="docMenu" [attr.aria-label]="'admin.actions' | translate">
                <i-tabler name="dots-vertical" class="icon-18"></i-tabler>
              </button>
              <mat-menu #docMenu="matMenu" xPosition="before">
                <button mat-menu-item (click)="openPdf(doc)"><i-tabler name="external-link" class="icon-18 m-r-8"></i-tabler>{{ 'documents.open_pdf' | translate }}</button>
                @if (doc.rag_status === 'error') {
                  <button mat-menu-item (click)="retry(doc)"><i-tabler name="refresh" class="icon-18 m-r-8"></i-tabler>{{ 'admin.docs_retry' | translate }}</button>
                }
                <button mat-menu-item (click)="rename(doc)"><i-tabler name="pencil" class="icon-18 m-r-8"></i-tabler>{{ 'admin.docs_rename' | translate }}</button>
                <button mat-menu-item (click)="move(doc)"><i-tabler name="folder-symlink" class="icon-18 m-r-8"></i-tabler>{{ 'admin.docs_move' | translate }}</button>
                <button mat-menu-item (click)="remove(doc)" [disabled]="doc.rag_status === 'deleting'"><i-tabler name="trash" class="icon-18 m-r-8"></i-tabler>{{ 'admin.docs_delete' | translate }}</button>
              </mat-menu>
            </td>
          </tr>
        }
      </tbody>
    </table>
  }
  @if (dragOver) {
    <div class="drop-overlay"><i-tabler name="cloud-upload" class="icon-32"></i-tabler>{{ 'documents.drop_active' | translate }}</div>
  }
</div>
<p class="hint">{{ 'admin.docs_hint' | translate }}</p>
